import React, { useEffect, useState } from 'react';
import { superApi, useSuperAdminAuth } from './SuperAdminAuth';
import { toast } from 'sonner';

export const SuperPlatformEmailPage = () => {
  const { hasPerm } = useSuperAdminAuth();
  const canManage = hasPerm('super.email.manage');
  const [cfg, setCfg] = useState({
    enabled: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: 'help@opswarex.com',
    from_name: 'Opswarex',
    use_tls: true,
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [log, setLog] = useState([]);

  const load = async () => {
    try {
      const [cfgR, logR] = await Promise.all([
        superApi.get('/super/email/config'),
        superApi.get('/super/email/log?limit=50').catch(() => ({ data: { entries: [] } })),
      ]);
      if (cfgR.data.configured) {
        setCfg({ ...cfg, ...cfgR.data, smtp_password: '' });
        setPasswordSet(cfgR.data.password_set);
      }
      setLog(logR.data.entries || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...cfg };
      if (!payload.smtp_password) delete payload.smtp_password;
      await superApi.put('/super/email/config', payload);
      toast.success('Config saved');
      setCfg({ ...cfg, smtp_password: '' });
      setPasswordSet(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error('Enter a test email');
    try {
      const r = await superApi.post('/super/email/test', { to_email: testEmail });
      if (r.data.sent) {
        toast.success('Test email sent');
        await load();
      } else {
        toast.error(r.data.error || 'Failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white">Platform Email</h1>
        <p className="text-sm text-slate-400 mt-1">SMTP/SES settings for all system emails (password resets, onboarding, billing alerts, broadcasts).</p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="From Email"><input className={inp} value={cfg.from_email} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} /></Field>
          <Field label="From Name"><input className={inp} value={cfg.from_name} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} /></Field>
          <Field label="SMTP Host"><input className={inp} placeholder="email-smtp.us-east-1.amazonaws.com" value={cfg.smtp_host} onChange={(e) => setCfg({ ...cfg, smtp_host: e.target.value })} /></Field>
          <Field label="SMTP Port"><input type="number" className={inp} value={cfg.smtp_port} onChange={(e) => setCfg({ ...cfg, smtp_port: parseInt(e.target.value || '587', 10) })} /></Field>
          <Field label="SMTP Username"><input className={inp} value={cfg.smtp_username} onChange={(e) => setCfg({ ...cfg, smtp_username: e.target.value })} /></Field>
          <Field label={`SMTP Password${passwordSet ? ' (leave blank to keep existing)' : ''}`}>
            <input type="password" className={inp} placeholder={passwordSet ? '••••••••' : ''} value={cfg.smtp_password} onChange={(e) => setCfg({ ...cfg, smtp_password: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-300 mt-4">
            <input type="checkbox" checked={cfg.use_tls} onChange={(e) => setCfg({ ...cfg, use_tls: e.target.checked })} /> Use TLS (recommended)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 mt-4">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} /> Enabled
          </label>
        </div>
        {canManage && (
          <div className="mt-6 flex gap-2">
            <button onClick={save} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium" data-testid="super-email-save">Save config</button>
          </div>
        )}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6">
        <div className="text-sm font-medium text-white mb-3">Send test email</div>
        <div className="flex gap-2">
          <input className={inp} value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" />
          <button onClick={sendTest} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium whitespace-nowrap" data-testid="super-email-test">Send test</button>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="text-sm font-medium text-white mb-3">Recent emails ({log.length})</div>
        <div className="max-h-80 overflow-y-auto">
          {log.length === 0 ? (
            <div className="text-xs text-slate-500">No emails sent yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-slate-500"><tr><th className="text-left p-2">When</th><th className="text-left p-2">To</th><th className="text-left p-2">Subject</th><th className="text-left p-2">Status</th></tr></thead>
              <tbody>
                {log.map((e) => (
                  <tr key={e.id} className="border-t border-slate-800">
                    <td className="p-2 text-slate-400">{new Date(e.timestamp).toLocaleString()}</td>
                    <td className="p-2 text-slate-300">{e.to_email}</td>
                    <td className="p-2 text-slate-300 truncate max-w-[300px]">{e.subject}</td>
                    <td className="p-2"><span className={e.status === 'sent' ? 'text-green-400' : 'text-red-400'}>{e.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const inp = 'w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white focus:outline-none focus:border-amber-500/50';
const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default SuperPlatformEmailPage;
